import { useCallback, useEffect, useRef } from 'react';
import type { ViewerPosition as Position } from '../contracts/models';
import { clientToContentPoint, clientToSvgUserPoint } from '../geometry/contentCoordinates';
import { clampViewportScale } from '../geometry/viewportScale';

export interface PanStartAnchor {
  clientX: number;
  clientY: number;
  positionX: number;
  positionY: number;
}

export interface InteractiveMarker {
  id: number;
  width: number;
  height: number;
  fontSize: number;
}

export interface MarkerAttributesUpdate {
  width: number;
  height: number;
  fontSize: number;
  aspectRatio: number;
}

export interface InteractionHandlersProps {
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  photoIndexMode: boolean;
  isPdfLayout: boolean;
  isDragging: boolean;
  scale: number;
  position: Position;
  displayPhotoMarkers: readonly InteractiveMarker[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  dragStartRef: React.MutableRefObject<PanStartAnchor | null>;
  lastDragPointRef: React.MutableRefObject<Position | null>;
  dragStartTimeRef: React.MutableRefObject<number>;
  touchStartDistanceRef: React.MutableRefObject<number | null>;
  touchStartScaleRef: React.MutableRefObject<number>;
  setIsDragging: (dragging: boolean) => void;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setLastSelectedMarkerId?: (id: number | null) => void;
  setIsHorizontalConstrained: (constrained: boolean) => void;
  setIsVerticalConstrained: (constrained: boolean) => void;
  addMeasurementPoint: (point: Position) => void;
  addPhotoMarker: (point: Position) => void;
  deleteSelectedMarkers?: () => void;
  copySelectedMarkers?: () => void;
  pasteMarkers?: () => void;
  undo?: () => void;
  onUpdateMarkerAttributes?: (attributes: MarkerAttributesUpdate) => void;
}

export const useInteractionHandlers = ({
  mode,
  photoIndexMode,
  isPdfLayout,
  isDragging,
  scale,
  position,
  displayPhotoMarkers,
  svgRef,
  groupRef,
  dragStartRef,
  lastDragPointRef,
  dragStartTimeRef,
  touchStartDistanceRef,
  touchStartScaleRef,
  setIsDragging,
  setPosition,
  setScale,
  setSelectedMarkers,
  setLastSelectedMarkerId,
  setIsHorizontalConstrained,
  setIsVerticalConstrained,
  addMeasurementPoint,
  addPhotoMarker,
  deleteSelectedMarkers,
  copySelectedMarkers,
  pasteMarkers,
  undo,
  onUpdateMarkerAttributes,
}: InteractionHandlersProps) => {
  const hadMultiTouchGestureRef = useRef(false);
  const pendingTapRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelGesture = useCallback(() => {
    if (pendingTapRef.current !== null) clearTimeout(pendingTapRef.current);
    pendingTapRef.current = null;
    dragStartRef.current = null;
    lastDragPointRef.current = null;
    dragStartTimeRef.current = 0;
    touchStartDistanceRef.current = null;
    hadMultiTouchGestureRef.current = false;
    setIsDragging(false);
    setIsHorizontalConstrained(false);
    setIsVerticalConstrained(false);
  }, [dragStartRef, lastDragPointRef, dragStartTimeRef, touchStartDistanceRef,
    setIsDragging, setIsHorizontalConstrained, setIsVerticalConstrained]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) cancelGesture();
    };
    // Safari can interrupt a gesture without delivering touchend/mouseup.
    document.addEventListener('touchcancel', cancelGesture, true);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', cancelGesture);
    window.addEventListener('pagehide', cancelGesture);
    return () => {
      document.removeEventListener('touchcancel', cancelGesture, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', cancelGesture);
      window.removeEventListener('pagehide', cancelGesture);
      if (pendingTapRef.current !== null) clearTimeout(pendingTapRef.current);
    };
  }, [cancelGesture]);

  const contentCoordinateOptions = useCallback(
    () => ({ isPdfLayout, position, scale }),
    [isPdfLayout, position, scale],
  );

  const getScaledPoint = useCallback((e: React.MouseEvent): Position | null => {
    if (!svgRef.current) return null;
    return clientToContentPoint(
      svgRef.current,
      e.clientX,
      e.clientY,
      contentCoordinateOptions(),
    );
  }, [svgRef, contentCoordinateOptions]);

  const panByPointerDelta = useCallback(
    (clientX: number, clientY: number, anchor: PanStartAnchor) => {
      if (isPdfLayout) {
        setPosition({
          x: anchor.positionX + (clientX - anchor.clientX),
          y: anchor.positionY + (clientY - anchor.clientY),
        });
        return;
      }

      if (!svgRef.current) return;
      const startUser = clientToSvgUserPoint(svgRef.current, anchor.clientX, anchor.clientY);
      const currentUser = clientToSvgUserPoint(svgRef.current, clientX, clientY);
      if (!startUser || !currentUser) return;

      setPosition({
        x: anchor.positionX + (currentUser.x - startUser.x),
        y: anchor.positionY + (currentUser.y - startUser.y),
      });
    },
    [isPdfLayout, setPosition, svgRef],
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode === 'view' || mode === 'measure' || mode === 'areaTags' || photoIndexMode) {
      if (
        e.target === svgRef.current ||
        e.target === groupRef.current ||
        (e.target as Element).tagName === 'image'
      ) {
        e.preventDefault();
        setIsDragging(false);
        dragStartTimeRef.current = Date.now();
        dragStartRef.current = {
          clientX: e.clientX,
          clientY: e.clientY,
          positionX: position.x,
          positionY: position.y,
        };
        lastDragPointRef.current = { x: e.clientX, y: e.clientY };
      }
    }
  }, [mode, photoIndexMode, position, svgRef, groupRef, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current) return;
    setIsDragging(true);
    panByPointerDelta(e.clientX, e.clientY, dragStartRef.current);
  }, [setIsDragging, dragStartRef, panByPointerDelta]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    lastDragPointRef.current = null;
    dragStartRef.current = null;
  }, [setIsDragging, lastDragPointRef, dragStartRef]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (mode === 'view' || mode === 'measure' || mode === 'areaTags' || photoIndexMode) {
      e.preventDefault();

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(false);
        dragStartTimeRef.current = Date.now();
        dragStartRef.current = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          positionX: position.x,
          positionY: position.y,
        };
        lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      } else if (e.touches.length === 2) {
        hadMultiTouchGestureRef.current = true;
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        touchStartDistanceRef.current = distance;
        touchStartScaleRef.current = scale;
        dragStartRef.current = null;
        lastDragPointRef.current = null;
      }
    }
  }, [mode, photoIndexMode, position, scale, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef, touchStartDistanceRef, touchStartScaleRef]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && dragStartRef.current) {
      e.preventDefault();
      const touch = e.touches[0];
      panByPointerDelta(touch.clientX, touch.clientY, dragStartRef.current);
      lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      setIsDragging(true);
    } else if (e.touches.length === 2 && touchStartDistanceRef.current !== null && touchStartScaleRef.current !== null) {
      e.preventDefault();
      hadMultiTouchGestureRef.current = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

      const scaleFactor = distance / touchStartDistanceRef.current;
      const newScale = clampViewportScale(touchStartScaleRef.current * scaleFactor);

      const midX = (touch1.clientX + touch2.clientX) / 2;
      const midY = (touch1.clientY + touch2.clientY) / 2;

      if (isPdfLayout) {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const parentX = midX - rect.left;
        const parentY = midY - rect.top;
        const groupX = (parentX - position.x) / scale;
        const groupY = (parentY - position.y) / scale;
        setPosition({
          x: parentX - groupX * newScale,
          y: parentY - groupY * newScale,
        });
      } else {
        if (!svgRef.current) return;
        const midpoint = clientToSvgUserPoint(svgRef.current, midX, midY);
        if (midpoint) {
          const groupX = (midpoint.x - position.x) / scale;
          const groupY = (midpoint.y - position.y) / scale;
          setPosition({
            x: midpoint.x - groupX * newScale,
            y: midpoint.y - groupY * newScale,
          });
        }
      }
      setScale(newScale);
    }
  }, [isPdfLayout, setPosition, setScale, setIsDragging, lastDragPointRef, dragStartRef, touchStartDistanceRef, touchStartScaleRef, svgRef, position, scale, panByPointerDelta]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 0) {
      if (!hadMultiTouchGestureRef.current && !isDragging && dragStartTimeRef.current) {
        const dragDuration = Date.now() - dragStartTimeRef.current;
        if (dragDuration < 200 && e.changedTouches.length > 0) {
          const touch = e.changedTouches[0];
          const target = document.elementFromPoint(touch.clientX, touch.clientY);

          if (target) {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
              clientX: touch.clientX,
              clientY: touch.clientY,
              screenX: touch.screenX,
              screenY: touch.screenY,
            });

            pendingTapRef.current = setTimeout(() => {
              pendingTapRef.current = null;
              if (!document.hidden && target.isConnected) target.dispatchEvent(clickEvent);
            }, 0);
          }
        }
      }

      setIsDragging(false);
      lastDragPointRef.current = null;
      dragStartRef.current = null;
      touchStartDistanceRef.current = null;
      hadMultiTouchGestureRef.current = false;
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      dragStartRef.current = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        positionX: position.x,
        positionY: position.y,
      };
      touchStartDistanceRef.current = null;
    }
  }, [isDragging, position, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef, touchStartDistanceRef]);

  const handleSvgClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element;
    if (target.closest('.photo-marker') || target.closest('.unit-polygon')) {
      return;
    }

    const dragDuration = Date.now() - (dragStartTimeRef.current || 0);
    const wasQuickClick = dragDuration < 200;

    if (mode === 'measure' && !isDragging && wasQuickClick) {
      const point = getScaledPoint(e);
      if (point) {
        addMeasurementPoint(point);
      }
    } else if (photoIndexMode && !isDragging && wasQuickClick) {
      const point = getScaledPoint(e);
      if (point) {
        addPhotoMarker(point);
      }
    } else if (photoIndexMode && !isDragging) {
      setSelectedMarkers(new Set());
    }
  }, [mode, photoIndexMode, isDragging, getScaledPoint, addMeasurementPoint, addPhotoMarker, setSelectedMarkers, dragStartTimeRef]);

  const handleWheelZoom = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    if (!svgRef.current) return;

    const zoomSpeed = 0.1;
    const factor = e.deltaY > 0 ? 1 - zoomSpeed : 1 + zoomSpeed;
    const newScale = clampViewportScale(scale * factor);

    if (newScale === scale) return;

    if (isPdfLayout) {
      const rect = svgRef.current.getBoundingClientRect();
      const parentX = e.clientX - rect.left;
      const parentY = e.clientY - rect.top;
      const groupX = (parentX - position.x) / scale;
      const groupY = (parentY - position.y) / scale;
      setScale(newScale);
      setPosition({
        x: parentX - groupX * newScale,
        y: parentY - groupY * newScale,
      });
      return;
    }

    const userPoint = clientToSvgUserPoint(svgRef.current, e.clientX, e.clientY);
    if (!userPoint) return;

    const groupX = (userPoint.x - position.x) / scale;
    const groupY = (userPoint.y - position.y) / scale;

    setScale(newScale);
    setPosition({
      x: userPoint.x - groupX * newScale,
      y: userPoint.y - groupY * newScale,
    });
  }, [isPdfLayout, scale, position, setScale, setPosition, svgRef]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (photoIndexMode) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
          e.preventDefault();
          if (undo) {
            undo();
          }
        }

        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

          if (!isInput) {
            e.preventDefault();
            const allMarkerIds = new Set(displayPhotoMarkers.map((m) => m.id));
            setSelectedMarkers(allMarkerIds);

            if (displayPhotoMarkers.length > 0 && setLastSelectedMarkerId) {
              const lastMarker = displayPhotoMarkers[displayPhotoMarkers.length - 1];
              setLastSelectedMarkerId(lastMarker.id);
            }

            if (displayPhotoMarkers.length > 0 && onUpdateMarkerAttributes) {
              const firstMarker = displayPhotoMarkers[0];
              onUpdateMarkerAttributes({
                width: firstMarker.width,
                height: firstMarker.height,
                fontSize: firstMarker.fontSize,
                aspectRatio: firstMarker.width / firstMarker.height,
              });
            }
          }
        }

        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

          if (!isInput) {
            e.preventDefault();
            if (copySelectedMarkers) {
              copySelectedMarkers();
            }
          }
        }

        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

          if (!isInput) {
            e.preventDefault();
            if (pasteMarkers) {
              pasteMarkers();
            }
          }
        }

        if (e.key === 'Delete' || e.key === 'Backspace') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

          if (!isInput) {
            e.preventDefault();
            if (deleteSelectedMarkers) {
              deleteSelectedMarkers();
            }
          }
        }
      }

      if (mode === 'measure') {
        if (e.shiftKey && e.key.toLowerCase() === 'h') {
          setIsHorizontalConstrained(true);
        }
        if (e.shiftKey && e.key.toLowerCase() === 'v') {
          setIsVerticalConstrained(true);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'h') {
        setIsHorizontalConstrained(false);
      }
      if (e.key.toLowerCase() === 'v') {
        setIsVerticalConstrained(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [photoIndexMode, mode, displayPhotoMarkers, setSelectedMarkers, setIsHorizontalConstrained, setIsVerticalConstrained, deleteSelectedMarkers, undo]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    handleMouseDown,
    handleSvgClick,
    handleWheelZoom,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getScaledPoint,
  };
};
